import { AuthorizationBase } from "./AuthorizationBase";

import { Account, UserAgentApplication } from "msal";
import { LoginType, MsalProvider } from "@microsoft/mgt";

import { AuthorizationModel, AccountMSAL } from "../models/Authorization";

import { authConfig } from "../configs/authConfig";
import {
  isIE,
  requiresInteraction,
  getAuthRoles,
  fetchMSGraph,
} from "../util/auth";
import { EnumHTTPMethod } from "../enum/EnumHTTPMethod";

interface MsalInstance {
  userAgentApplication: UserAgentApplication;
  scopes?: string[];
  loginType: LoginType;
}

const AUTH_SCOPES = {
  OPENID: "openid",
  OFFLINE_ACCESS: "offline_access",
  PROFILE: "profile",
  USER_READ: "user.read",
};

const useRedirectFlow = isIE();
const AUTH_REQUESTS = {
  LOGIN: {
    scopes: [AUTH_SCOPES.OPENID, AUTH_SCOPES.PROFILE, AUTH_SCOPES.USER_READ],
  },
  EMAIL: {
    scopes: [],
  },
  REFRESH_TOKEN: {
    scopes: [process.env.REACT_APP_CLIENT_ID],
  },
};

export class AuthorizationMSAL extends AuthorizationBase {
  userAgentApplication: UserAgentApplication;
  configurationMsalInstance: MsalInstance;
  msalInstance: MsalProvider;

  constructor() {
    super();

    this.userAgentApplication = new UserAgentApplication({
      auth: {
        clientId: authConfig.msal.appId,
        redirectUri: authConfig.msal.redirectUri,
        authority: authConfig.msal.authority,
        navigateToLoginRequestUrl: false,
      },
      cache: {
        cacheLocation: "localStorage",
        storeAuthStateInCookie: true,
      },
    });

    this.configurationMsalInstance = {
      userAgentApplication: this.userAgentApplication,
      scopes: ["user.read"],
      loginType: LoginType.Redirect,
    };

    this.msalInstance = new MsalProvider(this.configurationMsalInstance);

    this.acquireToken(AUTH_REQUESTS.REFRESH_TOKEN, useRedirectFlow);

    this.msalInstance.userAgentApplication.handleRedirectCallback(
      async (error: any) => {
        if (error) {
          console.error(error);
        }
      }
    );
  }

  private acquireToken(request: any, redirect: any) {
    return this.msalInstance.userAgentApplication
      .acquireTokenSilent(request)
      .then((response) => {
        this.authenticated = true;
        this.roles = getAuthRoles(response.account);
      })
      .catch((error: any) => {
        if (requiresInteraction(error.errorCode)) {
          this.authenticated = false;
          return redirect
            ? this.msalInstance.userAgentApplication.acquireTokenRedirect(
                request
              )
            : this.onSignIn();
        }

        console.error("Non-interactive error: ", error.errorCode);
        return false;
      });
  }

  onSignIn = () => {
    this.configurationMsalInstance.userAgentApplication.loginRedirect({
      scopes: [AUTH_SCOPES.OPENID, AUTH_SCOPES.PROFILE, AUTH_SCOPES.USER_READ],
      extraQueryParameters: {
        ui_locales: localStorage.getItem("langague") ?? "sv",
      },
    });
  };

  onSignOut = () => {
    this.configurationMsalInstance.userAgentApplication.logout();
  };

  getAccount = async () => {
    const user: Account = this.msalInstance.userAgentApplication.getAccount();

    const authModel: AuthorizationModel = {
      id_token: JSON.stringify(user.idToken) || "",
      access_token: "",
      expires_at: parseInt(user.idToken.exp) || 0,
      profile: {
        code: "",
        email: user.userName,
        name: user.userName.split("@")[0],
        organization: "",
        roles: [],
      },
    };

    return JSON.stringify(authModel);
  };

  async getTokenForScopes() {
    const scopeBackoffice = "api://in2.in2tant.backoffice.api/.default";
    const scopeShared = "api://in2.in2tant.shared.func/.default";

    const backoffice = await this.msalInstance.getAccessTokenForScopes(
      scopeBackoffice
    );
    const shared = await this.msalInstance.getAccessTokenForScopes(scopeShared);

    const item = Object.keys(localStorage).filter(
      (v) => v.startsWith("msal.") && v.endsWith("idtoken")
    );
    const idToken = localStorage.getItem(item[0]);

    return { backoffice: backoffice, shared: idToken || shared };
  }

  async onAccountStatus(status: string) {
    const { backoffice } = await this.getTokenForScopes();
    const body = JSON.stringify({ activity: status });

    const result = await fetchMSGraph(
      process.env.REACT_APP_API_URL + "/Presence",
      backoffice,
      EnumHTTPMethod.POST,
      body
    ).then((response) => response.json());

    this.accountStatus = result.activity;
  }
}
